import { useRef, useState } from 'react';
import { Button, Grid, IconButton, Tooltip } from '@material-ui/core';
import { ClassesType } from '../../../Classes.types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
	LeftPaneProps,
	uploadData,
} from '../Types/WhatsappCampaign.types';
import FilterRecipientsDialog from '../Popups/FilterRecipientsDialog';
import GroupSelector from './GroupSelector';
import { tabs } from '../../Constant';
import UploadXL from '../../../../components/Files/UploadXL';
import { UploadSettings } from '../../../../helpers/Constants';
import { BaseDialog } from '../../../../components/DialogTemplates/BaseDialog';
import Toast from '../../../../components/Toast/Toast.component';
import { BsInfoCircle } from 'react-icons/bs';

const LeftPane = ({
	classes,
	fetchPage,
	reloadToken,
	onSelectAllGroups,
	onFilterDialogOpen,
	totalRecipients,
	testGroupList,
	finishedCampaigns,
	selectedGroups,
	setSelected,
	selectedFilterCampaigns,
	setFilterCampaigns,
	selectedFilterGroups,
	setFilterGroups,
	onNewGroupChange,
	newGroupName,
	onNewGroupSave,
	activeTab,
	setActiveTab,
	onFilter,
	isCreateNewGroup,
	setIsCreateNewGroup,
	onManualUpload,
	exceptionalDaysToggle,
	exceptionalDays,
	setExceptionalDaysToggle,
	setExceptionalDays,
	showTestGroups,
	setShowTestGroups
}: ClassesType & LeftPaneProps) => {
	const { t: translator } = useTranslation();
	const [allGroupsSelected, setAllGroupsSelected] = useState<boolean>(false);
	const [dialogType, setDialogType] = useState<string>('');
	const refFilterRecipientsDialog = useRef<any>();
	const [toastMessage, setToastMessage] = useState(null);

	const onFilterSave = () => {
		setDialogType('');
		onFilter();
	};

	const renderToast = () => {
		if (toastMessage) {
			setTimeout(() => {
				setToastMessage(null);
			}, 3000);
			return (
				<Toast data={toastMessage} />
			);
		}
		return null;
	}

	const getFilterDialog = () => ({
		title: translator('campaigns.newsLetterEditor.sendSettings.filters'),
		showDivider: false,
		content: (
			<FilterRecipientsDialog
				isFilterModal={true}
				onFilterModalClose={() => setDialogType('')}
				classes={classes}
				fetchPage={fetchPage}
				reloadToken={reloadToken}
				finishedCampaigns={finishedCampaigns}
				selectedFilterCampaigns={selectedFilterCampaigns}
				setFilterCampaigns={setFilterCampaigns}
				selectedFilterGroups={selectedFilterGroups}
				setFilterGroups={setFilterGroups}
				onConfirmOrYes={onFilterSave}
				exceptionalDaysToggle={exceptionalDaysToggle}
				exceptionalDays={exceptionalDays}
				setExceptionalDaysToggle={setExceptionalDaysToggle}
				setExceptionalDays={setExceptionalDays}
				ref={refFilterRecipientsDialog}
			/>
		),
		showDefaultButtons: false,
		renderButtons: () =>
		(
			<Grid
				container
				spacing={2}
				className={clsx(classes.dialogButtonsContainer)}
			>
				<Grid item>
					<Button
						onClick={() => refFilterRecipientsDialog?.current?.onOkClick()}
						className={clsx(
							classes.btn,
							classes.btnRounded
						)}
					>
						{translator('common.Yes')}
					</Button>
				</Grid>
				<Grid item>
					<Button
						onClick={() => refFilterRecipientsDialog?.current?.onNoClick()}
						className={clsx(
							classes.btn,
							classes.btnRounded
						)}
					>
						{translator('common.No')}
					</Button>
				</Grid>
			</Grid>
		),
	})

	const renderDialog = () => {
		let currentDialog: any = {};
		if (dialogType === 'filter') {
			currentDialog = getFilterDialog();
		}

		if (dialogType) {
			return (
				dialogType && <BaseDialog
					classes={classes}
					open={dialogType}
					onCancel={() => setDialogType('')}
					onClose={() => setDialogType('')}
					renderButtons={currentDialog?.renderButtons || null}
					{...currentDialog}>
					{currentDialog?.content}
				</BaseDialog>
			)
		}
	}

	return (
		<Grid
			container
			direction='row'
			justifyContent='flex-start'
			className={classes.wizardFlex}>
			<Grid item md={12} xs={12} className={clsx(classes.mb20, classes.pt10)}>
				<span className={classes.conInfo}>
					{translator('mainReport.whomTosend')}
				</span>
				<Tooltip
					disableFocusListener
					title={translator('smsReport.whomtoSendTip')}
					classes={{ tooltip: classes.customWidth }}
				>
					<IconButton style={{ padding: 0 }} className={clsx(classes.icon_Info, classes.f20)}>
						<BsInfoCircle />
					</IconButton>
				</Tooltip>
			</Grid>
			<Grid item md={12} xs={12} className={classes.tabDiv}>
				<Grid
					item
					md={12}
					xs={12}
					className={clsx(classes.tab1, classes.btnTab, activeTab === tabs.GROUP ? classes.currentActiveTab : '')}
					onClick={() => setActiveTab(tabs.GROUP)}
				>
					<span style={{ cursor: 'pointer' }}>{translator('mainReport.groups')}</span>
				</Grid>
				<Grid
					item
					md={12}
					xs={12}
					className={clsx(classes.tab1, classes.btnTab, activeTab === tabs.MANUAL ? classes.currentActiveTab : '')}
					onClick={() => {
						setActiveTab(tabs.MANUAL);
						setIsCreateNewGroup(false);
					}}
				>
					<span style={{ marginInlineEnd: '7px', cursor: 'pointer' }} className={classes.elipsis}>{translator('mainReport.manual')}</span>
					<Tooltip
						disableFocusListener
						title={<>{translator('smsReport.manualTip')}</>}
						classes={{ tooltip: classes.customWidth }}
					>
						<IconButton style={{ padding: 0 }} className={clsx(classes.icon_Info, classes.f20)}>
							<BsInfoCircle />
						</IconButton>
					</Tooltip>
				</Grid>
			</Grid>
			{activeTab === tabs.MANUAL && (
				<UploadXL
					classes={classes}
					areaStyle={{
						height: 422,
					}}
					onDone={(
						groupName: string,
						res: uploadData,
						uploadedAsFile: boolean
					) => {
						onManualUpload(groupName, res, uploadedAsFile);
					}}
					settings={{ ...UploadSettings.GROUPS, ShowGroupName: true }}
					setToastMessage={setToastMessage}
					placeHolder={'recipient.addRecTextareaPlaceholder'}
					tooltipText='recipient.bulkRecUpldTooltipText'
					onlyMapping={true}
				/>
			)}
			{/* Hidden, not unmounted: the picker holds its loaded pages in state. */}
			<Grid
				item
				md={12}
				xs={12}
				style={{ display: activeTab === tabs.GROUP ? undefined : 'none' }}>
				<GroupSelector
					classes={classes}
					showTestGroups={showTestGroups}
					testGroupList={testGroupList}
					fetchPage={fetchPage}
					reloadToken={reloadToken}
					onSelectAllGroups={onSelectAllGroups}
					totalRecipients={totalRecipients}
					selectedGroups={selectedGroups}
					isFilterSelected={
						selectedFilterGroups?.length > 0 ||
						selectedFilterCampaigns?.length > 0 ||
						(exceptionalDaysToggle && exceptionalDays?.length > 0)
					}
					isCreateNewGroup={isCreateNewGroup}
					newGroupName={newGroupName}
					allGroupsSelected={allGroupsSelected}
					setIsCreateNewGroup={setIsCreateNewGroup}
					onNewGroupChange={onNewGroupChange}
					onNewGroupSave={onNewGroupSave}
					setSelected={setSelected}
					setIsFilterModal={() => {
						// The campaign name list is only needed by this dialog, so it
						// is fetched on open rather than on page load.
						if (onFilterDialogOpen) onFilterDialogOpen();
						setDialogType('filter');
					}}
					setAllGroupsSelected={setAllGroupsSelected}
					setShowTestGroups={setShowTestGroups}
				/>
			</Grid>
			{renderDialog()}
			{renderToast()}
		</Grid>
	);
};

export default LeftPane;
